import { type Campo, type Registro, TablaPivote } from '@/components/dominio/TablaPivote';
import { Card, CardContent } from '@/components/ui/card';
import { useAreaCosteoResumen, usePresupuestoPartidas } from '@/hooks/useAreaCosteo';
import { CircleNotch } from '@phosphor-icons/react';
import { useMemo } from 'react';

/** Indirecto = partida con prefijo "CI" (CI.PV, CI.SC). */
function esPartidaIndirecta(taskNo: string | null): boolean {
  return !!taskNo && taskNo.toUpperCase().startsWith('CI');
}

/**
 * Etiqueta de la partida. `tareaDisplay` suele venir ya con el código
 * ("1.1 - Fundaciones"); si es así, NO le anteponemos el código de nuevo.
 */
function etiquetaPartida(taskNo: string | null, display: string | null): string {
  if (display && taskNo && !display.startsWith(taskNo)) return `${taskNo} — ${display}`;
  return display ?? taskNo ?? '(—)';
}

/** Tarea padre = prefijo del código antes del primer punto ("1.1" → "1", "CI.PV" → "CI"). */
function tareaPadre(taskNo: string | null): string {
  if (!taskNo) return '(—)';
  return taskNo.split('.')[0] || taskNo;
}

const NOMBRE_MES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];
/** Etiqueta de mes que ordena cronológicamente: "03 Mar". */
function etiquetaMes(mes: number): string {
  return `${String(mes).padStart(2, '0')} ${NOMBRE_MES[mes - 1] ?? ''}`.trim();
}

/** Catálogo de campos disponibles en la pivot (dimensiones + métricas). */
const CAMPOS: Campo[] = [
  { id: 'Área', etiqueta: 'Área', tipo: 'dimension' },
  { id: 'Proyecto', etiqueta: 'Proyecto', tipo: 'dimension' },
  { id: 'Bloque', etiqueta: 'Bloque', tipo: 'dimension' },
  { id: 'Obra', etiqueta: 'Obra', tipo: 'dimension' },
  { id: 'Tipo Costo', etiqueta: 'Tipo Costo', tipo: 'dimension' },
  { id: 'Tarea Padre', etiqueta: 'Tarea Padre', tipo: 'dimension' },
  { id: 'Partida', etiqueta: 'Partida', tipo: 'dimension' },
  { id: 'Categoría', etiqueta: 'Categoría', tipo: 'dimension' },
  { id: 'Item', etiqueta: 'Item', tipo: 'dimension' },
  { id: 'Año', etiqueta: 'Año', tipo: 'dimension' },
  { id: 'Mes', etiqueta: 'Mes', tipo: 'dimension' },
  { id: 'Costo', etiqueta: 'Costo', tipo: 'metrica', formato: 'moneda' },
  { id: 'Cantidad', etiqueta: 'Cantidad', tipo: 'metrica', formato: 'numero' },
  // Métricas por partida (presupuesto/producción): se asignan a UNA fila por
  // partida (ver datos), así suman bien a nivel Partida/Obra/Proyecto.
  { id: 'Presupuesto', etiqueta: 'Presupuesto', tipo: 'metrica', formato: 'moneda' },
  { id: 'Producción', etiqueta: 'Producción', tipo: 'metrica', formato: 'moneda' },
];

/**
 * Tabla Dinámica de Costos: pivote configurable (drag-and-drop) con vista de
 * árbol colapsable. Reutiliza el dataset de Costos (endpoint area-costeo) y
 * respeta los filtros globales.
 */
export function PivotCostosPantalla() {
  const { data, isLoading, error } = useAreaCosteoResumen();
  const { data: presupuestoData } = usePresupuestoPartidas();

  // Presupuesto/Producción son por PARTIDA (obra+task_no): no tienen desglose
  // por ítem/mes. Mapa works_no|task_no → { presupuesto, producción }.
  const mapaPartida = useMemo(() => {
    const m = new Map<string, { presupuesto: number; produccion: number }>();
    for (const p of presupuestoData?.data ?? []) {
      m.set(`${p.worksNo}|${p.taskNo}`, {
        presupuesto: p.presupuestoCosto + p.presupuestoIndirecto,
        produccion: p.avancePct * p.presupuestoVenta,
      });
    }
    return m;
  }, [presupuestoData]);

  const datos = useMemo<Registro[]>(() => {
    // Para no duplicar, asignamos el valor por partida a la PRIMERA fila de esa
    // partida; las demás filas de la misma partida llevan 0.
    const vistas = new Set<string>();
    return (data?.data ?? []).map((r) => {
      const clavePartida = `${r.worksNo}|${r.taskNo ?? ''}`;
      const primeraDeLaPartida = !vistas.has(clavePartida);
      if (primeraDeLaPartida) vistas.add(clavePartida);
      const pres = mapaPartida.get(clavePartida);
      return {
        Área: r.areaCosteo,
        Proyecto: r.bloque,
        Bloque: r.subBloque ?? '(—)',
        Obra: r.proyectoDisplay ? `${r.worksNo} — ${r.proyectoDisplay}` : r.worksNo,
        'Tipo Costo': esPartidaIndirecta(r.taskNo) ? 'Indirecto' : 'Directo',
        'Tarea Padre': r.tareaPadreDisplay ?? tareaPadre(r.taskNo),
        Partida: etiquetaPartida(r.taskNo, r.tareaDisplay),
        Categoría: r.itemCategoryCode,
        Item: r.descripcionItem ?? '(—)',
        Año: String(r.anio),
        Mes: etiquetaMes(r.mes),
        Costo: r.totalCostoLcy,
        Cantidad: r.totalQuantity,
        Presupuesto: primeraDeLaPartida ? (pres?.presupuesto ?? 0) : 0,
        Producción: primeraDeLaPartida ? (pres?.produccion ?? 0) : 0,
      };
    });
  }, [data, mapaPartida]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
        Cargando…
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tabla Dinámica de Costos</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Arrastrá dimensiones a Filas/Columnas y métricas a Métricas. Elegí la agregación. Las
          filas se muestran como árbol colapsable con subtotales. Respeta los filtros globales.
        </p>
      </div>

      <TablaPivote
        datos={datos}
        campos={CAMPOS}
        filasIniciales={['Proyecto', 'Obra']}
        columnasIniciales={['Tipo Costo']}
        metricasIniciales={['Costo']}
        tituloExport="Tabla Dinámica de Costos"
        nombreArchivoExport="tabla-dinamica-costos"
        favoritasKey="costos"
      />
    </div>
  );
}
